import { outcomeOf } from '../catalog/pipeline/outcome';
import {
  isFailedDeployment,
  type DeploymentEnvironment,
  type DeploymentRecord,
} from './snapshot';

/**
 * The four figures over the Deployments page (specs/deployments.md AC-6, AC-7).
 *
 * Counted here, over the deploys the page lists, rather than read from the
 * snapshot's totals: the figures have to describe the rows beneath them, and
 * the change failure rate needs a split — production only — the totals do not
 * carry. They are counted over every row, never over the filtered ones
 * (AC-4): a reader narrowing the list is looking for deploys, not asking for a
 * different week.
 */
export type Figures = {
  deploys: number;
  repos: number;
  /**
   * Median seconds over the deploys that finished with a verdict; null when
   * none has.
   */
  medianSec: number | null;
  /**
   * The change failure rate's parts: production deploys that finished with a
   * verdict, and the failed among them.
   */
  production: { finished: number; failed: number };
};

/**
 * A deploy that finished with a verdict on the change: it succeeded or it
 * failed. A cancelled one was stopped, a running one has not finished, and one
 * GitHub calls neutral, stale or something this portal does not know says
 * nothing either way — so none of them is in the rate, and none of their
 * durations is in the median, where a deploy stopped after forty seconds would
 * pull the typical length of a deploy down.
 */
function hasVerdict(row: DeploymentRecord): boolean {
  const kind = outcomeOf(row).kind;
  return kind === 'success' || kind === 'failure';
}

export function figuresOf(rows: DeploymentRecord[]): Figures {
  const decided = rows.filter(hasVerdict);
  const durations = decided
    .map(row => row.durationSec)
    .filter((sec): sec is number => sec !== null)
    .sort((a, b) => a - b);
  const production = decided.filter(row => row.environment === 'production');
  return {
    deploys: rows.length,
    repos: new Set(rows.map(row => row.slug)).size,
    medianSec: median(durations),
    production: {
      finished: production.length,
      failed: production.filter(isFailedDeployment).length,
    },
  };
}

function median(sorted: number[]): number | null {
  if (!sorted.length) return null;
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/** "4%", "12.5%", "0.3%" — one decimal where rounding would hide a failure. */
export function formatRate(part: number, whole: number): string {
  const value = Math.round((part / whole) * 1000) / 10;
  return `${Number.isInteger(value) ? value : value.toFixed(1)}%`;
}

export type EnvironmentChoice = DeploymentEnvironment | 'all';

/**
 * The deploys the filters leave, newest first (AC-4, AC-5, and
 * specs/customer-projects.md AC-4).
 */
export function filterDeploys(
  rows: DeploymentRecord[],
  filters: {
    environment: EnvironmentChoice;
    onlyFailed: boolean;
    hideCustomerProjects: boolean;
  },
): DeploymentRecord[] {
  return rows
    .filter(
      row =>
        (filters.environment === 'all' ||
          row.environment === filters.environment) &&
        (!filters.onlyFailed || isFailedDeployment(row)) &&
        (!filters.hideCustomerProjects || !row.customerProject),
    )
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
}
